
import React from 'react';
import { Input } from '@/components/ui/input';
import { X, Navigation } from 'lucide-react';
import { LocationSuggestion, useLocationInput } from '@/hooks/ride/useLocationInput';
import LocationSuggestionsList from './LocationSuggestionsList';

interface LocationInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  icon: React.ReactNode;
  suggestions: LocationSuggestion[];
  recentLocations: string[];
  showCurrentLocation?: boolean;
  onCurrentLocation?: () => void;
  isGettingCurrentLocation?: boolean;
  isLoading: boolean;
  onSelect?: (value: string) => void;
  onCommit?: (value: string) => void;
}

const LocationInput: React.FC<LocationInputProps> = ({
  value,
  onChange,
  placeholder,
  icon,
  suggestions,
  recentLocations,
  showCurrentLocation = false,
  onCurrentLocation,
  isGettingCurrentLocation = false,
  isLoading,
  onSelect,
  onCommit,
}) => {
  const {
    inputValue,
    showPopover,
    inputRef,
    handleInputChange,
    handleClear,
    handleFocus,
    handleBlur,
    handleKeyDown,
    handleSelectLocation,
    containerRef,
  } = useLocationInput({
    value,
    onChange,
    recentLocations,
    onSelect,
    onCommit,
  });
  
  // Add aria attributes for improved accessibility
  const ariaAttributes = {
    'aria-label': placeholder,
    'aria-expanded': showPopover,
    'aria-haspopup': 'listbox' as React.AriaAttributes['aria-haspopup'],
    'aria-autocomplete': 'list' as React.AriaAttributes['aria-autocomplete'],
    'aria-controls': showPopover ? 'location-suggestions-list' : undefined
  };

  return (
    <div className="relative" ref={containerRef}>
      <div className="absolute left-0 top-0 h-full flex items-center pl-3 pointer-events-none z-10">
        {icon}
      </div>
      <div className="relative">
        <Input
          ref={inputRef}
          className="pl-10 pr-10 transition-all focus-visible:ring-offset-1 focus-visible:ring-primary/30 bg-background"
          placeholder={placeholder}
          value={inputValue}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          autoComplete="off" // Disable browser's native autocomplete
          role="combobox"
          {...ariaAttributes}
        />
        {inputValue && (
          <button
            className="absolute right-10 top-0 h-full flex items-center px-3 text-muted-foreground hover:text-foreground transition-colors"
            onClick={handleClear}
            type="button"
            aria-label="Clear input"
          >
            <X className="h-4 w-4" />
          </button>
        )}
        {showCurrentLocation && onCurrentLocation && (
          <button
            className="absolute right-0 top-0 h-full flex items-center px-3 text-muted-foreground hover:text-foreground transition-colors"
            onClick={onCurrentLocation}
            title="Use current location"
            type="button"
            aria-label="Use current location"
          >
            {isGettingCurrentLocation ? (
              <Navigation className="h-4 w-4 animate-spin" />
            ) : (
              <Navigation className="h-4 w-4" />
            )}
          </button>
        )}
      </div>

      {/*
        Anchored to the field rather than portalled. It is a combobox list, so
        it belongs to the input both visually and in the accessibility tree, and
        keeping it in the DOM here is what lets focus stay in the input without
        a dismissal layer fighting it.
      */}
      {showPopover && (
        <LocationSuggestionsList
          id="location-suggestions-list"
          inputValue={inputValue}
          suggestions={suggestions}
          recentLocations={recentLocations}
          showCurrentLocation={showCurrentLocation}
          onCurrentLocation={onCurrentLocation}
          isGettingCurrentLocation={isGettingCurrentLocation}
          isLoading={isLoading}
          onSelectLocation={handleSelectLocation}
        />
      )}
    </div>
  );
};

export default LocationInput;
